import { computed } from 'vue'
import { useRoute, type RouteLocationNormalizedLoaded } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ImportRunSource } from '@/api/generated-imports'
import type { BreadcrumbItem } from '@/components/molecules/Breadcrumbs.vue'

type Translate = ReturnType<typeof useI18n>['t']

interface TrailContext {
  t: Translate
  /** Dynamic label resolved by the route's `meta.breadcrumbLabel` (e.g. a client name). */
  label?: string
  params: RouteLocationNormalizedLoaded['params']
  /** The route's own meta, for a trail that depends on more than a label (see {@link setBreadcrumbImportSource}). */
  meta: RouteLocationNormalizedLoaded['meta']
}

type TrailBuilder = (ctx: TrailContext) => BreadcrumbItem[]

/** Resolves a dynamic label, falling back to a translated placeholder when absent. */
function dynamicLabel(ctx: TrailContext, fallbackKey: string): string {
  return ctx.label || ctx.t(`breadcrumbs.${fallbackKey}`)
}

/** Leading link for the organization-management hierarchy. */
function organizationLink(t: Translate): BreadcrumbItem {
  return { label: t('breadcrumbs.organizationManagement'), to: { name: 'organization' } }
}

/** Link back to the list page a detail route belongs to. */
function listLink(t: Translate, labelKey: string, listRoute: string): BreadcrumbItem {
  return { label: t(`breadcrumbs.${labelKey}`), to: { name: listRoute } }
}

/** Leading links for a route nested under an organization list page (e.g. a team detail page). */
function organizationSectionPrefix(t: Translate, labelKey: string, listRoute: string): BreadcrumbItem[] {
  return [organizationLink(t), listLink(t, labelKey, listRoute)]
}

/**
 * The import list a preview belongs to, per source of the run it shows (#4018).
 *
 * A preview is reached by run id whatever produced it (#3777), and the two
 * imports that produce one have lists of their own — so this middle crumb cannot
 * be a constant. A `Record` over every source rather than "is this a dump":
 * a third import added to the enum fails type-check here instead of silently
 * sending its runs to the EPD list. `EPD_DUMP` and the vendor-shaped sources are
 * the same delivery before and after recognition, and it was uploaded on the EPD
 * screen whichever EPD it came from.
 */
const IMPORT_PREVIEW_LISTS: Record<ImportRunSource, { labelKey: string; listRoute: string }> = {
  [ImportRunSource.AgendaPlanning]: {
    labelKey: 'agendaImport',
    listRoute: 'organization-agenda-import',
  },
  // A tariff sheet is uploaded from the contract it prices rather than from an
  // import screen of its own, so its preview leads back to the contract list —
  // the nearest thing this run has to a list it came from.
  [ImportRunSource.ContractTariffs]: { labelKey: 'contracts', listRoute: 'contracts' },
  [ImportRunSource.EpdDump]: { labelKey: 'epdImport', listRoute: 'organization-epd-import' },
  [ImportRunSource.ZorgadminDump]: { labelKey: 'epdImport', listRoute: 'organization-epd-import' },
  [ImportRunSource.MedicoreDump]: { labelKey: 'epdImport', listRoute: 'organization-epd-import' },
  [ImportRunSource.JamesDump]: { labelKey: 'epdImport', listRoute: 'organization-epd-import' },
}

/**
 * Publish the source of the run an import preview is showing, so its trail can
 * name the list that run came from (#4018).
 *
 * Written through here rather than by assigning the meta key in the view: a key
 * misspelt on either side leaves the middle crumb quietly absent, which is
 * exactly what the route looked like before this existed.
 */
export function setBreadcrumbImportSource(
  route: RouteLocationNormalizedLoaded,
  source: ImportRunSource | undefined,
): void {
  route.meta.breadcrumbImportSource = source
}

/**
 * Leading links for an import preview: Organisatiebeheer, then the list the run
 * came from once — and only once — that is known.
 *
 * The source is a fact about the run, so it does not exist until the preview has
 * been read. Until then the trail stops at Organisatiebeheer: a guessed middle
 * crumb links half of all previews to a list their import never came from, and
 * no link is better than a wrong one.
 */
function importPreviewPrefix(ctx: TrailContext): BreadcrumbItem[] {
  const list = IMPORT_PREVIEW_LISTS[ctx.meta.breadcrumbImportSource as ImportRunSource]
  if (!list) return [organizationLink(ctx.t)]
  return organizationSectionPrefix(ctx.t, list.labelKey, list.listRoute)
}

/** Leading link for the agenda hierarchy. */
function agendaLink(t: Translate): BreadcrumbItem {
  return { label: t('breadcrumbs.agenda'), to: { name: 'agenda' } }
}

/** Leading links shared by every client-scoped route: clients list -> client detail. */
function clientPrefix(ctx: TrailContext): BreadcrumbItem[] {
  const clientId = ctx.params.clientId as string
  return [
    listLink(ctx.t, 'clients', 'clients'),
    { label: dynamicLabel(ctx, 'fallbackClient'), to: { name: 'client-detail', params: { clientId } } },
  ]
}

/** Leading links for routes nested under a client's dossier. */
function clientDossierPrefix(ctx: TrailContext): BreadcrumbItem[] {
  const clientId = ctx.params.clientId as string
  return [
    ...clientPrefix(ctx),
    { label: ctx.t('breadcrumbs.dossier'), to: { name: 'client-dossier', params: { clientId } } },
  ]
}

/** Leading link for the claims (billing) hierarchy. */
function billingLink(t: Translate): BreadcrumbItem {
  return { label: t('breadcrumbs.billing'), to: { name: 'claims-insurer' } }
}

/**
 * Breadcrumb trail per route name. Each route declares its own trail builder,
 * so adding a route means adding one table entry — no linear branch growth.
 */
const TRAILS: Record<string, TrailBuilder> = {
  // Simple routes (no hierarchy)
  'dashboard': ({ t }) => [{ label: t('breadcrumbs.dashboard') }],
  'profile': ({ t }) => [{ label: t('breadcrumbs.profile') }],
  'signals': ({ t }) => [{ label: t('breadcrumbs.signals') }],
  'municipal-contract-detail': ({ t }) => [
    listLink(t, 'contracts', 'contracts'),
    { label: t('contracts.municipal.type') },
  ],
  'berichten': ({ t }) => [{ label: t('jeugdwet.berichten.title') }],
  'clients': ({ t }) => [{ label: t('breadcrumbs.clients') }],
  'agenda': ({ t }) => [{ label: t('breadcrumbs.agenda') }],
  'organization': ({ t }) => [{ label: t('breadcrumbs.organizationManagement') }],
  'contracts': ({ t }) => [{ label: t('breadcrumbs.contracts') }],

  // Organization hierarchy routes
  'organization-declareren': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.declareren') }],
  'organization-subscription': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.subscription') }],
  'organization-practice-details': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.practiceDetails') }],
  'organization-users': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.userManagement') }],
  'organization-user-detail': (ctx) => [
    ...organizationSectionPrefix(ctx.t, 'userManagement', 'organization-users'),
    { label: dynamicLabel(ctx, 'fallbackUser') },
  ],
  'organization-roles': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.roleManagement') }],
  'organization-documents': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.documents') }],
  'organization-security': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.security') }],
  'organization-teams': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.teamManagement') }],
  'organization-team-detail': (ctx) => [
    ...organizationSectionPrefix(ctx.t, 'teamManagement', 'organization-teams'),
    { label: dynamicLabel(ctx, 'fallbackTeam') },
  ],
  'organization-locations': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.locationManagement') }],
  'organization-location-detail': (ctx) => [
    ...organizationSectionPrefix(ctx.t, 'locationManagement', 'organization-locations'),
    { label: dynamicLabel(ctx, 'fallbackLocation') },
  ],
  'organization-appointment-types': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.appointmentTypes') }],
  'organization-appointment-type-detail': (ctx) => [
    ...organizationSectionPrefix(ctx.t, 'appointmentTypes', 'organization-appointment-types'),
    { label: dynamicLabel(ctx, 'fallbackAppointmentType') },
  ],
  'organization-client-states': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.clientStates') }],
  'organization-client-state-detail': (ctx) => [
    ...organizationSectionPrefix(ctx.t, 'clientStates', 'organization-client-states'),
    { label: dynamicLabel(ctx, 'fallbackClientState') },
  ],
  'organization-letter-templates': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.letterTemplates') }],
  'organization-letter-template-create': ({ t }) => [
    ...organizationSectionPrefix(t, 'letterTemplates', 'organization-letter-templates'),
    { label: t('breadcrumbs.newLetterTemplate') },
  ],
  'organization-letter-template-edit': (ctx) => [
    ...organizationSectionPrefix(ctx.t, 'letterTemplates', 'organization-letter-templates'),
    { label: dynamicLabel(ctx, 'fallbackLetterTemplate') },
  ],
  'organization-signal-definitions': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.signals') }],
  'organization-signal-authoring': ({ t }) => [
    ...organizationSectionPrefix(t, 'signals', 'organization-signal-definitions'),
    { label: t('breadcrumbs.signalAuthoring') },
  ],
  'organization-integrations': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.integrations') }],
  'organization-audit-log': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.auditLog') }],
  'organization-agenda-import': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.agendaImport') }],
  'organization-epd-import': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.epdImport') }],
  'organization-import-preview': (ctx) => [
    ...importPreviewPrefix(ctx),
    { label: ctx.t('breadcrumbs.importPreview') },
  ],

  // Forms management (question library + form builder), all under Organisatiebeheer
  'organization-forms-questions': ({ t }) => [
    organizationLink(t),
    { label: t('breadcrumbs.forms') },
    { label: t('breadcrumbs.questions') },
  ],
  'organization-forms-list': ({ t }) => [organizationLink(t), { label: t('breadcrumbs.forms') }],
  'organization-forms-detail': (ctx) => [
    ...organizationSectionPrefix(ctx.t, 'forms', 'organization-forms-list'),
    { label: dynamicLabel(ctx, 'fallbackForm') },
  ],
  'organization-form-settings': (ctx) => [
    ...organizationSectionPrefix(ctx.t, 'forms', 'organization-forms-list'),
    {
      label: dynamicLabel(ctx, 'fallbackForm'),
      to: { name: 'organization-forms-detail', params: { formId: ctx.params.formId as string } },
    },
    { label: ctx.t('breadcrumbs.formSettings') },
  ],

  // Client routes
  'client-detail': (ctx) => [
    listLink(ctx.t, 'clients', 'clients'),
    { label: dynamicLabel(ctx, 'fallbackClient') },
  ],
  'client-groups': ({ t }) => [{ label: t('breadcrumbs.clientGroups') }],
  'client-group-detail': (ctx) => [
    listLink(ctx.t, 'clientGroups', 'client-groups'),
    { label: dynamicLabel(ctx, 'fallbackClientGroup') },
  ],
  'client-care-trajectories': (ctx) => [...clientPrefix(ctx), { label: ctx.t('breadcrumbs.careTrajectories') }],
  'client-billing': (ctx) => [...clientPrefix(ctx), { label: ctx.t('breadcrumbs.financing') }],
  'client-dossier-overview': (ctx) => [...clientPrefix(ctx), { label: ctx.t('breadcrumbs.dossier') }],

  // Care trajectory detail
  'care-trajectory-detail': (ctx) => [
    ...clientPrefix(ctx),
    {
      label: ctx.t('breadcrumbs.careTrajectories'),
      to: { name: 'client-care-trajectories', params: { clientId: ctx.params.clientId as string } },
    },
    { label: ctx.t('breadcrumbs.careTrajectory') },
  ],

  // Client dossier sub-tabs
  'client-dossier-documents': (ctx) => [...clientDossierPrefix(ctx), { label: ctx.t('breadcrumbs.documents') }],
  'client-dossier-appointments': (ctx) => [...clientDossierPrefix(ctx), { label: ctx.t('breadcrumbs.appointments') }],
  'client-dossier-appointment-detail': (ctx) => [
    ...clientDossierPrefix(ctx),
    {
      label: ctx.t('breadcrumbs.appointments'),
      to: { name: 'client-dossier-appointments', params: { clientId: ctx.params.clientId as string } },
    },
    { label: ctx.t('breadcrumbs.appointment') },
  ],
  'client-dossier-answers': (ctx) => [...clientDossierPrefix(ctx), { label: ctx.t('breadcrumbs.answers') }],
  'client-dossier-zorgkring': (ctx) => [...clientDossierPrefix(ctx), { label: ctx.t('breadcrumbs.zorgkring') }],

  // Appointment detail (standalone route /appointments/:id without client context)
  'appointment-detail': ({ t }) => [agendaLink(t), { label: t('breadcrumbs.appointment') }],

  // Form submissions (filled-in forms, distinct from the form builder under Organisatiebeheer)
  'form-submissions': ({ t }) => [{ label: t('breadcrumbs.formSubmissions') }],
  'form-submission-detail': (ctx) => [
    listLink(ctx.t, 'formSubmissions', 'form-submissions'),
    { label: dynamicLabel(ctx, 'fallbackForm') },
  ],

  // Invoices and analytics
  'invoices': ({ t }) => [{ label: t('breadcrumbs.invoices') }],
  'invoice-detail': (ctx) => [
    listLink(ctx.t, 'invoices', 'invoices'),
    { label: dynamicLabel(ctx, 'fallbackInvoice') },
  ],
  'analytics': ({ t }) => [{ label: t('breadcrumbs.analytics') }],
  'analytics-declarabiliteit': ({ t }) => [
    listLink(t, 'analytics', 'analytics'),
    { label: t('breadcrumbs.declarabiliteit') },
  ],

  // Contract routes
  'contract-detail': (ctx) => [
    listLink(ctx.t, 'contracts', 'contracts'),
    { label: dynamicLabel(ctx, 'fallbackContract') },
  ],
  'contract-create': ({ t }) => [
    listLink(t, 'contracts', 'contracts'),
    { label: t('breadcrumbs.newContract') },
  ],
  'contract-edit': (ctx) => [
    listLink(ctx.t, 'contracts', 'contracts'),
    { label: dynamicLabel(ctx, 'fallbackContract') },
  ],

  // Claims (billing) routes
  'claims-insurer': ({ t }) => [
    { label: t('breadcrumbs.billing') },
    { label: t('breadcrumbs.healthInsurer') },
  ],
  'claims-private': ({ t }) => [billingLink(t), { label: t('breadcrumbs.private') }],
  'claims-invoices': ({ t }) => [billingLink(t), { label: t('breadcrumbs.invoices') }],
  'claims-batches': ({ t }) => [billingLink(t), { label: t('breadcrumbs.batches') }],
  'claims-municipality': ({ t }) => [billingLink(t), { label: t('jeugdwet.declaraties.tab') }],
}

/**
 * Routes that deliberately render without a breadcrumb trail: standalone auth and
 * MFA flows, and redirect-only routes that never render a page.
 *
 * `useBreadcrumbs.spec.ts` asserts that every behandelaar route is either in
 * `TRAILS` or listed here, so a new route can no longer silently hide the
 * breadcrumb bar the way `client-dossier-zorgkring` did (#3010, #3060).
 */
export const ROUTES_WITHOUT_BREADCRUMBS: readonly string[] = [
  'login',
  'forgot-password',
  'register',
  'accept-invitation',
  'impersonate',
  'mfa-setup',
  'mfa-challenge',
  // Redirect-only routes — they forward before a page (and its TopBar) renders.
  'client-dossier',
  'agenda-appointment',
]

export function useBreadcrumbs() {
  const route = useRoute()
  const { t } = useI18n()

  const breadcrumbs = computed((): BreadcrumbItem[] => {
    const builder = TRAILS[route.name as string]
    if (!builder) return []

    return builder({
      t,
      label: route.meta.breadcrumbLabel as string | undefined,
      params: route.params,
      meta: route.meta,
    })
  })

  return {
    breadcrumbs,
  }
}
